import {
  AgentPhase,
  AgentWidget,
  PresetOption,
  PresetActionId,
  SuggestedField,
} from "@/types/createAgent";
import { CreateFormData } from "@/components/create/types";
import { DRESS_STYLE_OPTIONS } from "@/utils/dress-style-options";
import { COLOR_PALETTES } from "@/components/create/constants";
import { BACK_PRESETS, FRONT_PRESETS } from "@/components/create/presets";
import { evaluateFrontDesignReadiness } from "@/lib/agent/designBriefReadiness";
import { hasBackUploadReference } from "@/lib/agent/actionReadinessGuards";
import {
  GENERATE_BACK_LABEL,
  GENERATE_FRONT_LABEL,
  MODIFY_BACK_LABEL,
  MODIFY_FRONT_LABEL,
} from "@/lib/agent/labels";
import { widgetActionFromPreset } from "@/lib/agent/widgetPlan";
import { isCategoryGridPresetAction } from "@/lib/agent/presetPresentation";
import { enrichColorData } from "@/utils/colorResolution";

/**
 * Helper to create a preset option with required actionId
 */
function createPreset(
  label: string,
  action: "suggest" | "action" | "modify",
  actionId: PresetActionId,
  data: Partial<CreateFormData> | Record<string, unknown> = {},
  actionData?: {
    generateType?: "front" | "back" | "modify";
    shouldCreateTicket?: boolean;
    shouldRequestLocation?: boolean;
    generateBack?: boolean;
    applyChanges?: boolean;
    suggestedField?: SuggestedField;
  }
): PresetOption {
  return { label, action, actionId, data: enrichColorData(data), actionData };
}

export function mergeSuggestionPresets(params: {
  basePresets: PresetOption[];
  suggestions?: string[];
  actionId: PresetActionId;
  allowSuggestions: boolean;
}): PresetOption[] {
  const { basePresets, suggestions, actionId, allowSuggestions } = params;
  if (!allowSuggestions || !suggestions || suggestions.length === 0) {
    return basePresets;
  }

  const sanitizeSuggestionLabel = (label: string): string | null => {
    const trimmed = label.replace(/[.?!]+$/, "").trim();
    if (trimmed.length === 0 || trimmed.length > 48) {
      return null;
    }
    const words = trimmed.split(/\s+/);
    if (words.length > 6) {
      return null;
    }
    // Reject occasion types—suggestions must be modification actions, not dress types
    const lower = trimmed.toLowerCase().replace(/ñ/g, "n");
    const occasionPatterns = [
      "wedding",
      "quinceanera",
      "prom",
      "bridesmaid",
      "formal",
      "bridal",
    ];
    if (occasionPatterns.some(p => lower.includes(p))) {
      return null;
    }

    return trimmed;
  };

  const existingLabels = new Set(
    basePresets.map(preset => preset.label.toLowerCase())
  );

  const suggestionPresets = suggestions
    .map(label => sanitizeSuggestionLabel(String(label)))
    .filter((label): label is string => !!label)
    .filter(label => !existingLabels.has(label.toLowerCase()))
    .slice(0, 3)
    .map(label =>
      createPreset(
        label,
        "modify",
        actionId,
        {},
        {
          generateType: "modify",
          applyChanges: true,
        }
      )
    );

  return [...basePresets, ...suggestionPresets];
}

/**
 * Match a friendly popular-option label (e.g. "V-neck", "A-Line", "Off-shoulder")
 * to its canonical catalog entry (e.g. "V - Classic V-neck opening"). Matching is
 * done against the entry's SHORT label (the part before " - ") — not the whole
 * string — so a label never matches on another entry's *description* (e.g.
 * "V-neck" must not resolve to "Plunge - Deep dramatic V-neck"). Comparison is
 * dash- and case-insensitive so hyphen seeds line up with en-dash catalog values
 * ("A-Line" → "A–Line - …", "Off-shoulder" → "Off–Shoulder - …").
 */
function findCatalogEntry(
  entries: readonly string[],
  label: string
): string | undefined {
  const canon = (value: string): string =>
    value
      .toLowerCase()
      // Strip ASCII hyphen and the unicode hyphen/dash block (U+2010–U+2015:
      // hyphen, non-breaking hyphen, figure dash, en dash, em dash, horizontal
      // bar) plus all whitespace so "A-Line" and "A–Line" canonicalize equally.
      .replace(/[-‐-―]/g, "")
      .replace(/\s+/g, "");
  const target = canon(label);
  return entries.find(entry => {
    const shortLabel = canon(entry.split(" - ")[0]);
    // Bidirectional prefix: handles "Ball Gown" → "Ball Gown / Princess …"
    // (catalog short label is longer) and "V-neck" → "V …" (seed is longer).
    return shortLabel.startsWith(target) || target.startsWith(shortLabel);
  });
}

/**
 * Helper to get popular options for a field
 * @param occasionAware - when true and preset_type is Wedding/Formal, use occasion-appropriate options (e.g. Cathedral train for wedding)
 */
export function getPopularOptions(
  field: "color" | "length" | "sleeve" | "neckline" | "silhouette" | "fabric",
  count: number = 3,
  occasionAware?: { preset_type?: string; prompt?: string }
): PresetOption[] {
  const isFormal =
    occasionAware?.preset_type &&
    /^(Wedding|Formal|Bridesmaid|Quinceanera|Prom)$/i.test(
      // Fold "ñ"→"n" so legacy "Quinceañera" preset_type rows still match.
      String(occasionAware.preset_type).replace(/ñ/g, "n")
    );
  const prompt = occasionAware?.prompt || "";
  const isModestContext = hasModestContext(prompt);
  const needsMobility = hasMobilityContext(prompt);
  const isWarmWeatherGuestContext = hasWarmWeatherGuestContext(prompt);

  switch (field) {
    case "color": {
      const options = getColorOptionsForPreset(occasionAware)
        .filter(
          color => !isRejectedColorSuggestion(color, occasionAware?.prompt)
        )
        .slice(0, count)
        .map(color => createPreset(color, "suggest", "set_color", { color }));
      return filterRejectedSuggestionPresets(options, occasionAware?.prompt);
    }
    case "length": {
      const lengths = isFormal
        ? DRESS_STYLE_OPTIONS.lengths.slice(-3).slice(0, count) // Chapel, Cathedral, Royal for formal
        : DRESS_STYLE_OPTIONS.lengths.slice(0, count);
      const options = lengths.map(length =>
        createPreset(length.split(" - ")[0], "suggest", "set_length", {
          length,
        })
      );
      return filterRejectedSuggestionPresets(options, prompt);
    }
    case "sleeve": {
      const sleeves = isFormal
        ? DRESS_STYLE_OPTIONS.sleeves
            .filter(
              s =>
                s.startsWith("Long") ||
                s.startsWith("Three") ||
                s.startsWith("Elbow")
            )
            .slice(0, count)
        : DRESS_STYLE_OPTIONS.sleeves.slice(0, count);
      const options = sleeves.map((sleeve: string) =>
        createPreset(sleeve.split(" - ")[0], "suggest", "set_sleeve", {
          sleeve,
        })
      );
      return filterRejectedSuggestionPresets(options, prompt);
    }
    case "neckline": {
      const popular = isModestContext
        ? ["High", "Boat", "Straight"]
        : isFormal
          ? ["High", "Sweetheart", "V-neck"] // High neck for formal/modest
          : ["Sweetheart", "V-neck", "Off-shoulder"];
      const options = popular.slice(0, count).map(neckline => {
        const fullOption = findCatalogEntry(
          DRESS_STYLE_OPTIONS.necklines,
          neckline
        );
        return createPreset(neckline, "suggest", "set_neckline", {
          neckline: fullOption || neckline,
        });
      });
      return filterRejectedSuggestionPresets(options, prompt);
    }
    case "silhouette": {
      const popular =
        needsMobility || isWarmWeatherGuestContext
          ? ["A-Line", "Empire"]
          : isModestContext
            ? ["A-Line", "Empire", "Ball Gown"]
            : ["Ball Gown", "A-Line", "Mermaid"];
      const options = popular.slice(0, count).map(silhouette => {
        const fullOption = findCatalogEntry(
          DRESS_STYLE_OPTIONS.silhouettes,
          silhouette
        );
        return createPreset(silhouette, "suggest", "set_silhouette", {
          silhouette: fullOption || silhouette,
        });
      });
      return filterRejectedSuggestionPresets(options, prompt);
    }
    case "fabric": {
      const popular =
        needsMobility || isWarmWeatherGuestContext
          ? ["Crepe de Chine", "Silk Charmeuse"]
          : ["Duchess Satin", "Silk Charmeuse", "Lace"];
      const options = popular.slice(0, count).map(fabric => {
        const fullOption = findCatalogEntry(
          DRESS_STYLE_OPTIONS.fabrics,
          fabric
        );
        return createPreset(fabric, "suggest", "set_fabric", {
          fabric: fullOption || fabric,
        });
      });
      return filterRejectedSuggestionPresets(options, prompt);
    }
  }
}

export type FullCatalogField =
  | "length"
  | "sleeve"
  | "neckline"
  | "silhouette"
  | "fabric"
  | "backPrompt";

const CATALOG_FIELD_LABELS: Record<FullCatalogField, string> = {
  length: "length",
  sleeve: "sleeve",
  neckline: "neckline",
  silhouette: "silhouette",
  fabric: "fabric",
  backPrompt: "back style",
};

function normalizeCatalogRequestText(value: string): string {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}

interface CatalogIntentClause {
  text: string;
  index: number;
}

interface FrontCatalogRequest {
  field: Exclude<FullCatalogField, "backPrompt">;
  index: number;
}

/**
 * Keep independent requests independent. A whole-message negative check made
 * "don't design the back; show me fabrics" suppress the valid second clause,
 * while a whole-message back check made the negated first clause win instead.
 */
function getCatalogIntentClauses(normalized: string): CatalogIntentClause[] {
  const clauses: CatalogIntentClause[] = [];
  const boundary =
    /[;.!?\n]+|[—–]+|\bbut\b|,\s*(?=(?:(?:and|then)\s+)?(?:show|display|open|browse|explore|list|give|tell|design|do|work|create|build|start|move|tackle)\b)/gi;
  let start = 0;

  const addClause = (end: number) => {
    const raw = normalized.slice(start, end);
    const firstNonWhitespace = raw.search(/\S/);
    if (firstNonWhitespace >= 0) {
      clauses.push({
        text: raw.trim(),
        index: start + firstNonWhitespace,
      });
    }
  };

  for (const match of normalized.matchAll(boundary)) {
    const index = match.index ?? 0;
    addClause(index);
    start = index + match[0].length;
  }
  addClause(normalized.length);
  return clauses;
}

const FRONT_CATALOG_FIELD_PATTERNS: ReadonlyArray<
  readonly [Exclude<FullCatalogField, "backPrompt">, RegExp]
> = [
  ["length", /\b(?:length|lengths|hemline|hemlines|train|trains)\b/g],
  ["sleeve", /\b(?:sleeve|sleeves|strap|straps)\b/g],
  ["neckline", /\b(?:neckline|neck line|necklines|neck lines)\b/g],
  ["silhouette", /\b(?:silhouette|silhouettes|dress shape|dress shapes)\b/g],
  ["fabric", /\b(?:fabric|fabrics|material|materials|textile|textiles)\b/g],
];

/**
 * Bind a browse request to the field nearest the browse language.
 *
 * The old implementation used one global "show/options/full" match and then
 * returned the first field found anywhere in the message. In a mixed turn such
 * as "square neckline and cap sleeves; show the full fabric catalog", that
 * made `neckline` authoritative even though the browse clause names fabric.
 * Proximity keeps the complete utterance available for detail extraction while
 * resolving the requested surface from the clause that actually asks to browse.
 */
function nearestRequestedFrontCatalogField(
  normalized: string
): FrontCatalogRequest | null {
  const field =
    "(?:lengths?|hemlines?|trains?|sleeves?|straps?|necklines?|neck lines?|silhouettes?|dress shapes?|fabrics?|materials?|textiles?)";
  const collection =
    "(?:options|choices|catalogs?|catalogues?|presets?|selections?)";
  const explicitNegative = new RegExp(
    `\\b(?:don['’]?t|do not|never|not now|no need to|stop)\\b[^.!?]{0,45}\\b(?:show|list|display|open|browse|explore|${field})\\b[^.!?]{0,35}\\b(?:${collection})?\\b`,
    "i"
  );
  const retrospectiveOrCommentary =
    /\b(?:already|previously|earlier|just)\s+(?:saw|seen|viewed|browsed|looked at|showed)|\b(?:options|choices|catalogs?|catalogues?)\b[^.!?]{0,24}\b(?:were|was|felt|seemed)\b|^\s*(?:why|how(?!\s+many\b))\b|\b(?:show|tell)\s+(?:me|us)\s+(?:how|why|whether|if)\b|\b(?:compare|explain|differ|difference between)\b/i;
  const requirementListing =
    /\blist\b[^.!?]{0,60}\brequirements?\b|\brequirements?\b[^.!?]{0,60}\blist\b/i;
  if (
    explicitNegative.test(normalized) ||
    retrospectiveOrCommentary.test(normalized) ||
    requirementListing.test(normalized)
  ) {
    return null;
  }

  const requestPatterns = [
    // Direct object only: "show me fabrics" qualifies; "show me how cap
    // sleeves would look" and "display the dress with a neckline" do not.
    new RegExp(
      `\\b(?:show|display|browse|explore)(?:\\s+(?:me|us))?\\s+(?:(?:the|all|full|complete|entire)\\s+){0,2}${field}(?:\\s+${collection})?\\b`,
      "gi"
    ),
    // A collection noun makes looser polite wording safe, including "give me
    // fabric options" without treating "give the dress cap sleeves" as browse.
    new RegExp(
      `\\b(?:show|display|open|browse|explore|list|give\\s+me|i\\s+(?:want|need)|i['’]?d\\s+like|can\\s+i\\s+see|could\\s+you\\s+show)\\b[^.!?]{0,40}\\b${field}\\b[^.!?]{0,18}\\b${collection}\\b`,
      "gi"
    ),
    new RegExp(
      `\\b(?:full|complete|entire|all)\\b[^.!?]{0,18}\\b${field}\\b[^.!?]{0,18}\\b${collection}\\b`,
      "gi"
    ),
    new RegExp(
      `\\b(?:what|which)\\b[^.!?]{0,24}\\b${field}\\b[^.!?]{0,24}\\bavailable\\b`,
      "gi"
    ),
    new RegExp(
      `\\b(?:what|which)\\b[^.!?]{0,24}\\b${field}\\b[^.!?]{0,18}\\b${collection}\\b[^.!?]{0,24}\\b(?:do\\s+you\\s+have|are\\s+there|can\\s+i\\s+see)\\b`,
      "gi"
    ),
    new RegExp(
      `\\b(?:show|display|open|browse|explore|list)(?:\\s+(?:me|us))?\\s+(?:(?:the|all|full|complete|entire)\\s+){0,2}${collection}\\s+(?:for|of)\\s+(?:the\\s+)?${field}\\b`,
      "gi"
    ),
    // Concise UI-style shorthand: "Fabric options, please".
    new RegExp(
      `^\\s*(?:the\\s+)?${field}\\s+${collection}(?:\\s*,?\\s*please)?[.!]*\\s*$`,
      "gi"
    ),
  ];
  const requestSignals = requestPatterns.flatMap(pattern =>
    Array.from(normalized.matchAll(pattern), match => ({
      index: match.index ?? 0,
      penalty: 0,
    }))
  );
  if (requestSignals.length === 0) return null;

  const candidates: Array<{
    field: Exclude<FullCatalogField, "backPrompt">;
    distance: number;
    index: number;
  }> = [];
  for (const [field, pattern] of FRONT_CATALOG_FIELD_PATTERNS) {
    pattern.lastIndex = 0;
    for (const match of normalized.matchAll(pattern)) {
      const index = match.index ?? 0;
      const distance = Math.min(
        ...requestSignals.map(
          signal => Math.abs(index - signal.index) + signal.penalty
        )
      );
      candidates.push({ field, distance, index });
    }
  }

  candidates.sort(
    (left, right) =>
      left.distance - right.distance ||
      // A later field usually belongs to the final request clause ("show the
      // full fabric catalog"), while earlier fields describe the dress.
      right.index - left.index
  );
  const requested = candidates[0];
  return requested
    ? {
        field: requested.field,
        index: requested.index,
      }
    : null;
}

function isExplicitBackRenderRequest(normalized: string): boolean {
  const back = "(?:the\\s+|a\\s+|my\\s+|our\\s+)?back";
  return (
    new RegExp(`\\b(?:generate|render|preview)\\s+${back}\\b`, "i").test(
      normalized
    ) ||
    new RegExp(
      `\\b(?:create|make|build|start)\\b[^.!?;]{0,35}\\b${back}\\s+(?:preview|render|image|view|mockup)\\b`,
      "i"
    ).test(normalized) ||
    new RegExp(
      `\\b(?:design|do|work\\s+on|create|build|start|move\\s+to|move\\s+onto|tackle)\\b[^.!?;]{0,35}\\b${back}\\b[^.!?;]{0,35}\\b(?:and|then|,\\s*then)\\s+(?:generate|render|preview)\\b`,
      "i"
    ).test(normalized)
  );
}

function requestedBackCatalogIndex(clause: string): number | null {
  const backCollection =
    "(?:styles?|designs?|closures?|details?|options?|choices?|catalogs?|catalogues?|presets?)";
  const directBackRequest =
    /\b(?:design|do|work on|working on|create|build|start|move to|onto|tackle)\s+(?:the\s+|a\s+|my\s+|our\s+)?back\b/i;
  const explicitBrowsePatterns = [
    new RegExp(
      `\\b(?:show|list|display|view|see|give|open|browse|explore)\\b[^.!?;]{0,45}\\b(?:the\\s+|my\\s+|our\\s+)?back\\b[^.!?;]{0,30}\\b${backCollection}\\b`,
      "i"
    ),
    new RegExp(
      `\\b(?:show|list|display|view|see|give|open|browse|explore)\\b[^.!?;]{0,35}\\b${backCollection}\\b[^.!?;]{0,20}\\b(?:for|of)\\s+(?:the|my|our)\\s+back\\b`,
      "i"
    ),
    new RegExp(
      `\\bback\\b[^.!?;]{0,30}\\b${backCollection}\\b[^.!?;]{0,35}\\b(?:show|list|display|view|see|available|browse|explore|${backCollection})\\b`,
      "i"
    ),
    new RegExp(
      `^\\s*(?:what|which)\\b[^.!?;]{0,30}\\bback\\b[^.!?;]{0,20}\\b${backCollection}\\b[^.!?;]{0,30}\\b(?:available|do\\s+you\\s+have|are\\s+there)\\b`,
      "i"
    ),
    new RegExp(
      `^\\s*(?:what\\s+are\\s+(?:my|the|our)|show\\s+me|let\\s+me\\s+see)\\s+back\\s+${backCollection}\\b`,
      "i"
    ),
    new RegExp(
      `^\\s*(?:do\\s+you\\s+have|are\\s+there)\\b[^.!?;]{0,24}\\bback\\s+${backCollection}\\b`,
      "i"
    ),
    new RegExp(
      `^\\s*(?:the\\s+)?back\\s+${backCollection}(?:\\s*,?\\s*please)?\\s*$`,
      "i"
    ),
  ];
  const negatedOrDeferred =
    /\b(?:don['’]?t|do not|never|not ready(?:\s+to)?|rather not|hold off|no need to|stop|wait(?:\s+to)?)\b[^.!?;]{0,55}\bback\b|\bback\b[^.!?;]{0,30}\b(?:later|not yet)\b/i;
  const informationalOrCommentary =
    /^\s*(?:why|when|how(?!\s+many\b)|should|did|does|is|are|will)\b|^\s*do\s+(?:we|i|you|they)\s+(?:need|have\s+to|want|plan|intend)\b|\b(?:show|tell)\s+(?:me|us)\s+(?:how|why|whether|if)\b|\b(?:compare|explain|differ|difference between)\b|\b(?:already|previously|earlier|just)\b[^.!?;]{0,45}\b(?:back|design(?:ed)?|brows(?:e|ed))\b|\b(?:love|like|approve|approved|perfect|happy with)\b[^.!?;]{0,35}\bback\b/i;
  const positiveAvailabilityQuestion = new RegExp(
    `^\\s*(?:do\\s+you\\s+have|are\\s+there)\\b[^.!?;]{0,24}\\bback\\s+${backCollection}\\b`,
    "i"
  ).test(clause);
  if (
    negatedOrDeferred.test(clause) ||
    (informationalOrCommentary.test(clause) && !positiveAvailabilityQuestion)
  ) {
    return null;
  }

  for (const pattern of explicitBrowsePatterns) {
    const match = pattern.exec(clause);
    if (match) return match.index;
  }

  const direct = directBackRequest.exec(clause);
  if (
    direct &&
    /^\s*(?:can|could|would)\b|^\s*do\s+(?:we|i|you|they)\b/i.test(clause)
  ) {
    return null;
  }
  return direct?.index ?? null;
}

export function getRequestedFullCatalogField(params: {
  userMessage: string;
  currentPhase: AgentPhase;
  hasFrontImage: boolean;
  hasBackImage?: boolean;
}): FullCatalogField | null {
  const normalized = normalizeCatalogRequestText(params.userMessage);
  const skipBackCatalog = isExplicitBackRenderRequest(normalized);
  const canBrowseBack =
    !params.hasBackImage &&
    (params.hasFrontImage || params.currentPhase === "back_design");
  let latestRequest: { field: FullCatalogField; index: number } | null = null;

  for (const clause of getCatalogIntentClauses(normalized)) {
    const frontRequest = nearestRequestedFrontCatalogField(clause.text);
    if (frontRequest) {
      latestRequest = {
        field: frontRequest.field,
        index: clause.index + frontRequest.index,
      };
    }

    if (!skipBackCatalog && canBrowseBack) {
      const backIndex = requestedBackCatalogIndex(clause.text);
      const absoluteBackIndex =
        backIndex === null ? null : clause.index + backIndex;
      if (
        absoluteBackIndex !== null &&
        (!latestRequest || absoluteBackIndex > latestRequest.index)
      ) {
        latestRequest = { field: "backPrompt", index: absoluteBackIndex };
      }
    }
  }

  return latestRequest?.field ?? null;
}

export function getFullCatalogRequestPhase(params: {
  field: FullCatalogField;
  currentPhase: AgentPhase;
}): AgentPhase {
  if (params.field === "backPrompt") return "back_design";
  if (params.currentPhase === "initial") return "front_design";
  return params.currentPhase;
}

export function buildFullCatalogRequestMessage(
  field: FullCatalogField
): string {
  return `Here are the ${CATALOG_FIELD_LABELS[field]} options. Choose one to add it to your design.`;
}

function isFullCatalogField(value: unknown): value is FullCatalogField {
  return (
    value === "length" ||
    value === "sleeve" ||
    value === "neckline" ||
    value === "silhouette" ||
    value === "fabric" ||
    value === "backPrompt"
  );
}

function fullCatalogFieldForActionId(
  actionId: PresetActionId
): FullCatalogField | null {
  switch (actionId) {
    case "set_length":
      return "length";
    case "set_sleeve":
      return "sleeve";
    case "set_neckline":
      return "neckline";
    case "set_silhouette":
      return "silhouette";
    case "set_fabric":
      return "fabric";
    case "set_back_prompt":
    case "set_back_style":
      return "backPrompt";
    default:
      return null;
  }
}

function shortCatalogLabel(value: string): string {
  return value.split(" - ")[0].trim();
}

export function getFullCatalogOptions(
  field: FullCatalogField,
  options?: { prompt?: string }
): PresetOption[] {
  let presets: PresetOption[];

  switch (field) {
    case "length":
      presets = DRESS_STYLE_OPTIONS.lengths.map(length =>
        createPreset(shortCatalogLabel(length), "suggest", "set_length", {
          length,
        })
      );
      break;
    case "sleeve":
      presets = [
        ...DRESS_STYLE_OPTIONS.sleeves.map(sleeve =>
          createPreset(shortCatalogLabel(sleeve), "suggest", "set_sleeve", {
            sleeve,
          })
        ),
        ...DRESS_STYLE_OPTIONS.sleeveAddons.map(sleeveAddon =>
          createPreset(
            shortCatalogLabel(sleeveAddon),
            "suggest",
            "set_sleeve",
            {
              sleeveAddons: [sleeveAddon],
            }
          )
        ),
      ];
      break;
    case "neckline":
      presets = DRESS_STYLE_OPTIONS.necklines.map(neckline =>
        createPreset(shortCatalogLabel(neckline), "suggest", "set_neckline", {
          neckline,
        })
      );
      break;
    case "silhouette":
      presets = DRESS_STYLE_OPTIONS.silhouettes.map(silhouette =>
        createPreset(
          shortCatalogLabel(silhouette),
          "suggest",
          "set_silhouette",
          {
            silhouette,
          }
        )
      );
      break;
    case "fabric":
      presets = DRESS_STYLE_OPTIONS.fabrics.map(fabric =>
        createPreset(shortCatalogLabel(fabric), "suggest", "set_fabric", {
          fabric,
        })
      );
      break;
    case "backPrompt":
      presets = BACK_PRESETS.filter(
        preset => preset.label !== "Custom" && preset.label !== "Upload"
      ).map(preset =>
        createPreset(preset.label, "suggest", "set_back_prompt", {
          backPrompt: preset.value,
        })
      );
      break;
  }

  return filterRejectedSuggestionPresets(presets, options?.prompt);
}

function getSingleFullCatalogField(
  presets: PresetOption[]
): FullCatalogField | null {
  const suggestionFields = presets
    .filter(
      preset =>
        preset.action === "suggest" &&
        isCategoryGridPresetAction(preset.actionId)
    )
    .map(preset => fullCatalogFieldForActionId(preset.actionId))
    .filter((field): field is FullCatalogField => !!field);

  if (suggestionFields.length === 0) return null;
  const uniqueFields = new Set(suggestionFields);
  return uniqueFields.size === 1 ? suggestionFields[0] : null;
}

function hasModestContext(prompt: string): boolean {
  return /\b(?:modest|coverage|covered|not too revealing|arms covered|sleeves?|high neck|conservative)\b/i.test(
    prompt
  );
}

function hasMobilityContext(prompt: string): boolean {
  return /\b(?:dance|move|mobility|comfortable|hate tight|not tight|avoid tight|doesn'?t cling|do not cling|flowy|roomy)\b/i.test(
    prompt
  );
}

function hasWarmWeatherGuestContext(prompt: string): boolean {
  return /\b(?:summer|hot|garden|breathable|wedding guest|guest dress|not floor length)\b/i.test(
    prompt
  );
}

function normalizePresetType(value: string | undefined): string {
  return normalizeForSuggestionMatch(value || "");
}

function getPaletteKey(
  presetType: string | undefined
): keyof typeof COLOR_PALETTES | null {
  const normalized = normalizePresetType(presetType);
  if (!normalized) return null;
  if (normalized.includes("wedding") || normalized.includes("bridal"))
    return "wedding";
  if (normalized.includes("formal")) return "formal";
  if (normalized.includes("bridesmaid")) return "bridesmaid";
  if (normalized.includes("quince")) return "quinceanera";
  if (normalized.includes("prom")) return "prom";
  return null;
}

function getPromptColorSuggestions(prompt: string | undefined): string[] {
  if (!prompt?.trim()) return [];

  const normalizedPrompt = normalizeForSuggestionMatch(prompt);
  const suggestions: string[] = [];
  const pushIfPresent = (label: string, patterns: string[]) => {
    if (patterns.some(pattern => normalizedPrompt.includes(pattern))) {
      suggestions.push(label);
    }
  };

  pushIfPresent("Sage Green", ["sage green", "sage"]);
  pushIfPresent("Dusty Blue", ["dusty blue"]);
  pushIfPresent("Dusty Rose", ["dusty rose"]);
  pushIfPresent("Blush", ["blush"]);
  pushIfPresent("Navy", ["navy"]);
  pushIfPresent("Burgundy", ["burgundy", "wine", "oxblood"]);
  pushIfPresent("Emerald", ["emerald"]);
  pushIfPresent("Black", ["black"]);
  pushIfPresent("Champagne", ["champagne"]);
  pushIfPresent("Ivory", ["ivory"]);
  pushIfPresent("White", ["white", "alabaster"]);

  return uniqueColorLabels(suggestions);
}

function uniqueColorLabels(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const key = normalizeForSuggestionMatch(value);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(value);
  }
  return result;
}

function getColorOptionsForPreset(occasionAware?: {
  preset_type?: string;
  prompt?: string;
}): string[] {
  const paletteKey = getPaletteKey(occasionAware?.preset_type);
  const preferred = paletteKey ? COLOR_PALETTES[paletteKey] : [];
  const fallback = Object.values(COLOR_PALETTES).flat();
  const paletteLabels = [...preferred, ...fallback]
    .filter(color => color.popular)
    .map(color => color.name);

  return uniqueColorLabels([
    ...getPromptColorSuggestions(occasionAware?.prompt),
    ...paletteLabels,
  ]);
}

function normalizeForSuggestionMatch(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const COLOR_REJECTION_ALIASES: Record<string, string[]> = {
  white: [
    "white",
    "ivory",
    "cream",
    "alabaster",
    "soft white",
    "snow",
    "porcelain",
  ],
  gold: ["gold", "champagne", "gilded"],
};

function getColorRejectionAliases(colorName: string): string[] {
  const normalizedColor = normalizeForSuggestionMatch(colorName);

  for (const group of Object.values(COLOR_REJECTION_ALIASES)) {
    const normalizedGroup = group.map(normalizeForSuggestionMatch);
    if (normalizedGroup.includes(normalizedColor)) {
      return normalizedGroup;
    }
  }

  return [
    normalizedColor,
    ...(COLOR_REJECTION_ALIASES[normalizedColor] || []).map(
      normalizeForSuggestionMatch
    ),
  ].filter(Boolean);
}

const FIELD_REJECTION_ALIASES: Partial<Record<PresetActionId, string[][]>> = {
  set_neckline: [
    ["plunge", "plunging", "deep v", "deep neckline", "deep neck"],
    ["off shoulder", "off shoulder neckline", "off shoulder neck"],
    ["sweetheart", "sweetheart neckline"],
    ["v neck", "v neckline", "v-neck", "v-neckline"],
    ["scoop", "scoop neckline"],
    ["halter", "halter neckline"],
    ["high neck", "high neckline"],
    ["boat neck", "boat neckline", "bateau"],
    ["square neck", "square neckline"],
    ["straight neck", "straight neckline"],
    ["collared", "collared neckline"],
  ],
  set_silhouette: [
    ["mermaid"],
    ["princess", "ball gown", "ballgown"],
    ["a line", "a-line", "aline"],
    ["sheath", "column"],
    ["body hugging", "body-hugging", "tight"],
    ["drop waist", "drop-waist"],
  ],
  set_fabric: [
    ["sparkle", "sparkly", "sequin", "sequins", "glitter"],
    ["beaded", "beading", "heavy beading"],
    ["lace"],
    ["satin"],
    ["tulle"],
    ["velvet"],
    ["heavy fabric", "stiff fabric"],
  ],
};

function presetSearchText(preset: PresetOption): string {
  const values: string[] = [preset.label];
  const visit = (value: unknown) => {
    if (typeof value === "string") {
      values.push(value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (value && typeof value === "object") {
      Object.values(value).forEach(visit);
    }
  };
  visit(preset.data);
  return normalizeForSuggestionMatch(values.join(" "));
}

function normalizedTextContainsAlias(text: string, alias: string): boolean {
  const normalizedAlias = normalizeForSuggestionMatch(alias);
  if (!normalizedAlias) return false;
  return new RegExp(`\\b${normalizedAlias}\\b`, "i").test(text);
}

function promptRejectsAlias(prompt: string, alias: string): boolean {
  const normalizedPrompt = normalizeForSuggestionMatch(prompt);
  const normalizedAlias = normalizeForSuggestionMatch(alias);
  if (!normalizedPrompt || !normalizedAlias) return false;

  const beforeAlias = new RegExp(
    `\\b(?:no|not|never|without|exclude|drop|remove|avoid|dislike|hate|dont like|don t like|do not like|dont want|don t want|do not want|not into)(?:\\s+\\w+){0,6}\\s+${normalizedAlias}\\b`,
    "i"
  );
  const afterAlias = new RegExp(
    `\\b${normalizedAlias}\\b(?:\\s+\\w+){0,4}\\s+(?:removed|out|excluded)\\b`,
    "i"
  );
  return (
    beforeAlias.test(normalizedPrompt) || afterAlias.test(normalizedPrompt)
  );
}

function isRejectedPresetSuggestion(
  preset: PresetOption,
  prompt: string | undefined
): boolean {
  if (!prompt?.trim()) return false;
  if (preset.actionId === "set_color") {
    return isRejectedColorSuggestion(preset.label, prompt);
  }

  const aliasGroups = FIELD_REJECTION_ALIASES[preset.actionId] || [];
  if (aliasGroups.length === 0) return false;

  const presetText = presetSearchText(preset);
  return aliasGroups.some(
    group =>
      group.some(alias => promptRejectsAlias(prompt, alias)) &&
      group.some(alias => normalizedTextContainsAlias(presetText, alias))
  );
}

function filterRejectedSuggestionPresets(
  presets: PresetOption[],
  prompt: string | undefined
): PresetOption[] {
  if (!prompt?.trim()) return presets;
  return presets.filter(
    preset =>
      preset.action !== "suggest" || !isRejectedPresetSuggestion(preset, prompt)
  );
}

function isRejectedColorSuggestion(
  colorName: string,
  prompt: string | undefined
): boolean {
  if (!prompt?.trim()) return false;

  const normalizedPrompt = normalizeForSuggestionMatch(prompt);
  const normalizedAliases = getColorRejectionAliases(colorName);

  return normalizedAliases.some(alias => {
    const beforeColor = new RegExp(
      `\\b(?:no|not|non|without|avoid|exclude|drop|remove|dislike|hate|dont like|don t like|do not like|dont want|don t want|do not want|move away from)(?:\\s+\\w+){0,6}\\s+${alias}\\b`,
      "i"
    );
    const afterColor = new RegExp(
      `\\b${alias}\\b(?:\\s+\\w+){0,4}\\s+(?:removed|out|excluded)\\b`,
      "i"
    );
    return (
      beforeColor.test(normalizedPrompt) || afterColor.test(normalizedPrompt)
    );
  });
}

export interface GeneratePresetsOptions {
  /** Explicit image flags—use when form state may not have imageUrl/backImageUrl */
  hasFrontImage?: boolean;
  hasBackImage?: boolean;
}

/**
 * Generates contextual presets based on phase and collected data.
 * Returns 3-5 most relevant presets based on context.
 * Use options.hasFrontImage/hasBackImage when form state may not include image URLs.
 */
export function generatePresets(
  phase: AgentPhase,
  collectedData: Partial<CreateFormData>,
  options?: GeneratePresetsOptions
): PresetOption[] {
  const presets: PresetOption[] = [];
  const hasFrontImage =
    options?.hasFrontImage ?? Boolean((collectedData as any).imageUrl);
  const hasBackImage =
    options?.hasBackImage ?? Boolean((collectedData as any).backImageUrl);
  // A back image can only exist once the front was generated and approved (back
  // generation is sequential on the front), so its presence alone implies front
  // approval — even in a restored/partial state that is missing the front image
  // URL. Requiring hasFrontImage here previously stranded back_review with zero
  // presets (no quote, no modify) when only the back URL survived.
  const frontApproved = !!collectedData.frontApproved || hasBackImage;
  const isLoggedIn = collectedData.isLoggedIn === true;
  const hasCredits = collectedData.hasCredits;
  const shouldShowBuyCredits = isLoggedIn && hasCredits === false;
  const virtualTryOnPreset = createPreset(
    "Try it on",
    "action",
    "start_virtual_try_on"
  );

  // Phase-specific preset generation
  if (phase === "initial") {
    return FRONT_PRESETS.filter(
      preset => preset.label !== "Custom" && preset.label !== "Upload"
    )
      .slice(0, 5)
      .map(preset =>
        createPreset(preset.label, "suggest", "set_preset_type", {
          prompt: preset.value,
          preset_type: preset.label,
        })
      );
  }

  if (phase === "front_design") {
    const readiness = evaluateFrontDesignReadiness(collectedData, {
      hasFrontImage,
    });

    // When occasion is set but the brief is still sparse, wait for more design
    // detail before offering a generate action.
    const hasOccasion = !!collectedData.preset_type;
    if (
      hasOccasion &&
      readiness.concreteFieldCount < 2 &&
      !readiness.readyToGenerate
    ) {
      return presets;
    }

    // Once we have enough info and no front image, show Generate button (stays until front image generated)
    if (readiness.readyToGenerate) {
      presets.push(
        createPreset(
          GENERATE_FRONT_LABEL,
          "action",
          "generate_dress_front",
          {},
          { generateType: "front" }
        )
      );
    }

    // Add default presets for next missing field (using priority order)
    const nextMissingField = readiness.missingFields[0];

    if (nextMissingField) {
      const nextOptions = isFullCatalogField(nextMissingField)
        ? getFullCatalogOptions(nextMissingField, {
            prompt: collectedData.prompt as string | undefined,
          })
        : getPopularOptions(nextMissingField, 3, {
            preset_type: collectedData.preset_type as string | undefined,
            prompt: collectedData.prompt as string | undefined,
          });
      presets.push(...nextOptions);
    }

    return nextMissingField && isFullCatalogField(nextMissingField)
      ? presets
      : presets.slice(0, 5);
  }

  if (phase === "front_review") {
    if (!hasFrontImage) {
      return presets;
    }
    if (shouldShowBuyCredits) {
      return [
        createPreset(
          "Get free quote",
          "action",
          "submit_quote",
          {},
          { shouldCreateTicket: true }
        ),
        createPreset(
          "Buy credits to design back",
          "action",
          "buy_credits_back"
        ),
        virtualTryOnPreset,
      ];
    }

    const backStylePresets = getFullCatalogOptions("backPrompt", {
      prompt: collectedData.prompt as string | undefined,
    }).map(preset => ({
      ...preset,
      data: {
        ...preset.data,
        frontApproved: true,
        generateBack: true,
      },
    }));

    return [
      createPreset(
        "Get free quote",
        "action",
        "submit_quote",
        {},
        { shouldCreateTicket: true }
      ),
      virtualTryOnPreset,
      createPreset("Design the back", "action", "continue_to_back_design", {
        frontApproved: true,
        generateBack: true,
      }),
      ...backStylePresets,
    ];
  }

  if (phase === "back_design") {
    if (!hasFrontImage) {
      return presets;
    }
    if (!frontApproved) return [virtualTryOnPreset];
    // Always show Get free quote when front image is generated
    const quotePreset = createPreset(
      "Get free quote",
      "action",
      "submit_quote",
      {},
      { shouldCreateTicket: true }
    );
    if (collectedData.backPrompt || hasBackUploadReference(collectedData)) {
      return [
        quotePreset,
        virtualTryOnPreset,
        createPreset(GENERATE_BACK_LABEL, "action", "generate_dress_back"),
      ];
    }

    return [
      quotePreset,
      virtualTryOnPreset,
      ...getFullCatalogOptions("backPrompt", {
        prompt: collectedData.prompt as string | undefined,
      }),
    ];
  }

  if (phase === "back_review") {
    if (!hasFrontImage && !hasBackImage) {
      return presets;
    }
    if (!hasBackImage || !frontApproved) {
      return hasFrontImage ? [virtualTryOnPreset] : presets;
    }
    if (shouldShowBuyCredits) {
      return [
        createPreset(
          "Get free quote",
          "action",
          "submit_quote",
          {},
          { shouldCreateTicket: true }
        ),
        createPreset(
          "Buy credits to design more",
          "action",
          "buy_credits_modify"
        ),
        ...(hasFrontImage ? [virtualTryOnPreset] : []),
      ];
    }

    const backModificationPresets = [
      createPreset(
        "Lower the back",
        "modify",
        "modify_dress_back",
        { backPrompt: "Lower the back" },
        { generateType: "modify", applyChanges: true }
      ),
      createPreset(
        "Add more lace",
        "modify",
        "modify_dress_back",
        { backPrompt: "Add more lace to the back" },
        { generateType: "modify", applyChanges: true }
      ),
      createPreset(
        "Change to zip-up",
        "modify",
        "modify_dress_back",
        {
          backPrompt:
            "Clean zip-up back with invisible zipper for easy wear and modern appeal",
        },
        { generateType: "modify", applyChanges: true }
      ),
    ];

    return [
      createPreset(
        "Get free quote",
        "action",
        "submit_quote",
        {},
        { shouldCreateTicket: true }
      ),
      ...(hasFrontImage ? [virtualTryOnPreset] : []),
      ...backModificationPresets,
    ].slice(0, 5);
  }

  if (phase === "finalization") {
    return [
      ...(hasFrontImage ? [virtualTryOnPreset] : []),
      ...(hasFrontImage && hasBackImage && frontApproved
        ? [createPreset("Submit quote", "action", "submit_quote")]
        : []),
    ];
  }

  return presets;
}

/**
 * Repair a planner-staged Generate turn that otherwise lost its visible CTA.
 * Readiness and prose never authorize this path by themselves: the caller must
 * prove the turn already requested the front render. This stages the paid
 * action; it never executes it.
 */
export function ensureReadyFrontGeneratePreset(params: {
  phase: AgentPhase;
  formState: Partial<CreateFormData>;
  message: string;
  presets?: PresetOption[];
  widgets?: AgentWidget[];
  hasFrontImage?: boolean;
  hasBackImage?: boolean;
  /**
   * Repair permission from the turn planner. Readiness and declarative copy
   * alone are never consent to offer a paid render; this fallback may only
   * restore a Generate surface for a turn that already staged that action.
   */
  allowGenerateFallback?: boolean;
}): PresetOption[] | undefined {
  const existingPresets = Array.isArray(params.presets)
    ? params.presets
    : undefined;
  if (existingPresets && existingPresets.length > 0) return existingPresets;

  const hasInteractiveWidget = (params.widgets ?? []).some(
    widget =>
      widget.active &&
      ((widget.presets?.length ?? 0) > 0 || (widget.actions?.length ?? 0) > 0)
  );
  if (
    hasInteractiveWidget ||
    params.allowGenerateFallback !== true ||
    params.phase !== "front_design" ||
    params.hasFrontImage ||
    /[?？]/.test(params.message)
  ) {
    return existingPresets;
  }

  const generatePreset = generatePresets("front_design", params.formState, {
    hasFrontImage: params.hasFrontImage,
    hasBackImage: params.hasBackImage,
  }).find(preset => preset.actionId === "generate_dress_front");

  return generatePreset ? [generatePreset] : existingPresets;
}

/**
 * Widgets own the renderer whenever they are present. Mirror the front
 * Generate preset into that authoritative surface so a summary-only widget
 * cannot hide the only way forward, whether the preset was authored or added
 * by the ready-state fallback above.
 */
export function ensureReadyFrontGenerateActionSurface(params: {
  phase: AgentPhase;
  formState: Partial<CreateFormData>;
  message: string;
  presets?: PresetOption[];
  widgets?: AgentWidget[];
  hasFrontImage?: boolean;
  hasBackImage?: boolean;
  allowGenerateFallback?: boolean;
}): { presets?: PresetOption[]; widgets?: AgentWidget[] } {
  const presets = ensureReadyFrontGeneratePreset(params);
  const widgets = params.widgets;
  const visibleGeneratePreset = presets?.find(
    preset => preset.actionId === "generate_dress_front"
  );

  if (!visibleGeneratePreset || !widgets || widgets.length === 0) {
    return { presets, widgets };
  }

  const isRepresented = widgets.some(
    widget =>
      widget.active &&
      [...(widget.presets ?? []), ...(widget.actions ?? [])].some(
        option =>
          option.actionId === visibleGeneratePreset.actionId &&
          option.label === visibleGeneratePreset.label
      )
  );
  if (isRepresented) return { presets, widgets };

  return {
    presets,
    widgets: [
      ...widgets,
      {
        id: "ready-front-generate-action",
        version: 1,
        type: "action_dock",
        active: true,
        title: "Preview your dress",
        presets: [visibleGeneratePreset],
        actions: [widgetActionFromPreset(visibleGeneratePreset, 0)],
      },
    ],
  };
}

/** Required action presets that must always appear for each phase */
const FRONT_REVIEW_ACTIONS: PresetOption[] = [
  createPreset(
    "Get free quote",
    "action",
    "submit_quote",
    {},
    {
      shouldCreateTicket: true,
    }
  ),
  createPreset("Design the back", "action", "continue_to_back_design", {
    frontApproved: true,
    generateBack: true,
  }),
];
const BACK_DESIGN_GENERATE: PresetOption = createPreset(
  GENERATE_BACK_LABEL,
  "action",
  "generate_dress_back",
  {},
  { generateType: "back" }
);
const FRONT_DESIGN_GENERATE: PresetOption = createPreset(
  GENERATE_FRONT_LABEL,
  "action",
  "generate_dress_front",
  {},
  { generateType: "front" }
);

/**
 * Re-run front generation using the latest form state (applies any changes the user
 * described since the front image was generated). Uses modify_dress_front so the handler
 * preserves the existing image's identity while applying accumulated edits.
 */
const FRONT_REVIEW_REGENERATE: PresetOption = createPreset(
  MODIFY_FRONT_LABEL,
  "modify",
  "modify_dress_front",
  {},
  { generateType: "modify", applyChanges: true }
);

/** Same idea for the back image. */
const BACK_REVIEW_REGENERATE: PresetOption = createPreset(
  MODIFY_BACK_LABEL,
  "modify",
  "modify_dress_back",
  {},
  { generateType: "modify", applyChanges: true }
);

/**
 * Single source of truth for agent presets. Use this from collectInfoAgent.
 * Ensures required actions (Get free quote, Design the back, Generate back) are always shown.
 */
export function getPresetsForAgent(params: {
  phase: AgentPhase;
  formState: Partial<CreateFormData>;
  hasFrontImage: boolean;
  hasBackImage: boolean;
  /** Vibe or suggest presets from LLM—when provided, used instead of base suggest presets */
  llmPresets?: PresetOption[];
  /** Modification suggestions from image review (converted to modify presets) */
  modificationSuggestions?: string[];
  reviewSide?: "front" | "back";
  allowModificationSuggestions?: boolean;
  /** When true (e.g. preset click), ensure Generate is shown when we have full front data but no image */
  ensureGenerateWhenReady?: boolean;
}): PresetOption[] {
  const {
    phase,
    formState,
    hasFrontImage,
    hasBackImage,
    llmPresets = [],
    modificationSuggestions = [],
    reviewSide = "front",
    allowModificationSuggestions = false,
    ensureGenerateWhenReady = false,
  } = params;

  // Ensure form state has image URLs for generatePresets (it may infer from these)
  const formStateWithImages = {
    ...formState,
    ...(hasFrontImage &&
      !(formState as any).imageUrl && { imageUrl: "present" }),
    ...(hasBackImage &&
      !(formState as any).backImageUrl && { backImageUrl: "present" }),
  };

  const basePresets = generatePresets(phase, formStateWithImages, {
    hasFrontImage,
    hasBackImage,
  });
  const actionPresets = basePresets.filter(p => p.action === "action");
  const suggestPresetsFromBase = basePresets.filter(
    p => p.action === "suggest"
  );

  const vibePresets = llmPresets.filter(
    p =>
      p.actionId === "custom" &&
      p.data &&
      typeof p.data === "object" &&
      Object.keys(p.data).length >= 3
  );
  const nonVibeFromLlm = llmPresets.filter(
    p =>
      !(
        p.actionId === "custom" &&
        p.data &&
        typeof p.data === "object" &&
        Object.keys(p.data).length >= 3
      )
  );

  const suggestPresetsToUse =
    vibePresets.length > 0
      ? vibePresets
      : nonVibeFromLlm.length > 0
        ? nonVibeFromLlm
        : suggestPresetsFromBase;
  const fullCatalogField =
    vibePresets.length === 0
      ? getSingleFullCatalogField(suggestPresetsToUse)
      : null;
  const expandedSuggestPresetsToUse = fullCatalogField
    ? getFullCatalogOptions(fullCatalogField, {
        prompt: formState.prompt as string | undefined,
      })
    : suggestPresetsToUse;

  const existingLabels = new Set([
    ...actionPresets.map(p => (p.label || "").toLowerCase()),
    ...expandedSuggestPresetsToUse.map(p => (p.label || "").toLowerCase()),
  ]);
  const filteredNonVibe =
    vibePresets.length > 0
      ? nonVibeFromLlm.filter(
          p => !existingLabels.has((p.label || "").toLowerCase())
        )
      : [];

  let presets: PresetOption[] = [
    ...actionPresets,
    ...expandedSuggestPresetsToUse,
    ...filteredNonVibe,
  ];

  // Always ensure front_review actions when we have a front image
  if (phase === "front_review" && hasFrontImage) {
    for (const req of FRONT_REVIEW_ACTIONS) {
      if (!presets.some(p => p.actionId === req.actionId)) {
        presets = [req, ...presets];
      }
    }
  }

  // Always ensure Generate back when in back_design with backPrompt
  if (
    phase === "back_design" &&
    hasFrontImage &&
    formState.frontApproved &&
    (formState.backPrompt || hasBackUploadReference(formState)) &&
    !presets.some(p => p.actionId === "generate_dress_back")
  ) {
    presets = [BACK_DESIGN_GENERATE, ...presets];
  }

  // Ensure Generate/Regenerate when the user explicitly asked for an action
  // (preset click OR typed "generate the front"/"make it"/etc.).
  if (ensureGenerateWhenReady) {
    if (!hasFrontImage) {
      const readiness = evaluateFrontDesignReadiness(formState, {
        hasFrontImage,
      });
      if (
        readiness.readyToGenerate &&
        !presets.some(p => p.actionId === "generate_dress_front")
      ) {
        presets = [FRONT_DESIGN_GENERATE, ...presets];
      }
    } else if (phase === "front_review") {
      // Already have a front image—"generate"/"regenerate" requests map to modify_dress_front
      // so the existing image is updated with any changes the user described.
      if (!presets.some(p => p.actionId === "modify_dress_front")) {
        presets = [FRONT_REVIEW_REGENERATE, ...presets];
      }
    } else if (phase === "back_review" && hasBackImage) {
      if (!presets.some(p => p.actionId === "modify_dress_back")) {
        presets = [BACK_REVIEW_REGENERATE, ...presets];
      }
    }
  }

  // Add modification suggestions for image review
  if (allowModificationSuggestions && modificationSuggestions.length > 0) {
    const modifyId =
      reviewSide === "back" ? "modify_dress_back" : "modify_dress_front";
    presets = mergeSuggestionPresets({
      basePresets: presets,
      suggestions: modificationSuggestions,
      actionId: modifyId as PresetActionId,
      allowSuggestions: true,
    });
  }

  const shouldPreserveFullCatalog =
    !!getSingleFullCatalogField(presets) &&
    presets.filter(preset => preset.action === "suggest").length > 5;

  return shouldPreserveFullCatalog ? presets : presets.slice(0, 5);
}
